import {
  DEFAULT_EXCHANGE_AVAILABILITY,
  EXCHANGE_TO_MARKET_SOURCE,
  SPOT_EXCHANGES,
  type ExchangeAvailability,
  type ExchangeKey,
  type MarketSource,
  type PartialExchangeAvailability,
} from '@frontend-shared/exchanges/types'

import type { PortalProfile } from '../auth/types'

export { DEFAULT_EXCHANGE_AVAILABILITY, EXCHANGE_TO_MARKET_SOURCE, SPOT_EXCHANGES }
export type { ExchangeAvailability, ExchangeKey, MarketSource, PartialExchangeAvailability }

type MarketSourceStatusEntry = {
  status?: string | null
} | null | undefined

type MarketSourceStatuses = Record<string, MarketSourceStatusEntry> | null | undefined

const MARKET_SOURCE_TO_EXCHANGE: Record<string, ExchangeKey> = Object.fromEntries(
  Object.entries(EXCHANGE_TO_MARKET_SOURCE).map(([exchange, source]) => [source, exchange as ExchangeKey])
)

const normalizeExchangeKey = (exchange: unknown): ExchangeKey | null => {
  const normalized = String(exchange ?? '').trim().toLowerCase()
  return SPOT_EXCHANGES.includes(normalized as ExchangeKey) ? (normalized as ExchangeKey) : null
}

const sanitizeExchangeAvailability = (
  value: PartialExchangeAvailability | Record<string, unknown> | undefined
): PartialExchangeAvailability => {
  if (!value || typeof value !== 'object') {
    return {}
  }

  const normalized: PartialExchangeAvailability = {}
  for (const exchange of SPOT_EXCHANGES) {
    const entry = value[exchange]
    if (typeof entry === 'boolean') {
      normalized[exchange] = entry
    }
  }
  return normalized
}

const normalizeMarketSource = (source: unknown): string => String(source ?? '').trim().toUpperCase()

const isStatusLoaded = (statusLoaded: boolean | undefined): boolean => statusLoaded !== false

export const getExchangeAvailability = (profile: PortalProfile | null | undefined): ExchangeAvailability => {
  const settingsAvailability = sanitizeExchangeAvailability(profile?.settings?.exchangeAvailability)
  const profileAvailability = sanitizeExchangeAvailability(profile?.exchangeAvailability)

  return {
    ...DEFAULT_EXCHANGE_AVAILABILITY,
    ...settingsAvailability,
    ...profileAvailability,
  }
}

export const isExchangeAllowed = (
  profile: PortalProfile | null | undefined,
  exchange: unknown
): boolean => {
  const key = normalizeExchangeKey(exchange)
  if (!key) {
    return false
  }
  return getExchangeAvailability(profile)[key] !== false
}

/**
 * Wyszukiwanie frachtów w Timocom — flaga admina `settings.timocomFreightSearchEnabled`, bez niej wyłączone.
 * Lustro backendu TimocomFreightSearchSettings.isFreightSearchEnabled (boolean albo napis "true"); na tej fladze
 * SourceAvailabilityService daje Timocomowi NO_FLAG w trybie frachtów.
 */
export const isTimocomFreightSearchEnabled = (profile: PortalProfile | null | undefined): boolean => {
  const flag: unknown = profile?.settings?.timocomFreightSearchEnabled
  return flag === true || (typeof flag === 'string' && flag.trim().toLowerCase() === 'true')
}

export const getAllowedExchanges = (profile: PortalProfile | null | undefined): ExchangeKey[] =>
  SPOT_EXCHANGES.filter((exchange) => isExchangeAllowed(profile, exchange))

export const filterAllowedExchanges = (
  profile: PortalProfile | null | undefined,
  exchanges: unknown
): ExchangeKey[] => {
  const values = Array.isArray(exchanges) ? exchanges : exchanges != null ? [exchanges] : []
  const normalized: ExchangeKey[] = []

  for (const exchange of values) {
    const key = normalizeExchangeKey(exchange)
    if (!key || !isExchangeAllowed(profile, key) || normalized.includes(key)) {
      continue
    }
    normalized.push(key)
  }

  return normalized
}

export const getMarketSourceRuntimeStatus = (
  sourceStatuses: MarketSourceStatuses,
  source: unknown
): string => {
  const key = normalizeMarketSource(source)
  if (!key) {
    return ''
  }
  return String(sourceStatuses?.[key]?.status || '').trim().toLowerCase()
}

export const isMarketSourceOnline = (
  sourceStatuses: MarketSourceStatuses,
  source: unknown
): boolean => getMarketSourceRuntimeStatus(sourceStatuses, source) === 'online'

export const isExchangeEffectivelyAvailable = (
  profile: PortalProfile | null | undefined,
  exchange: unknown,
  sourceStatuses: MarketSourceStatuses,
  options: { statusLoaded?: boolean } = {}
): boolean => {
  const key = normalizeExchangeKey(exchange)
  if (!key || !isExchangeAllowed(profile, key)) {
    return false
  }
  if (!isStatusLoaded(options.statusLoaded)) {
    return true
  }
  return isMarketSourceOnline(sourceStatuses, EXCHANGE_TO_MARKET_SOURCE[key])
}

/**
 * Giełdy RENDEROWANE w sekcji „Miejsce publikacji": dozwolone licencyjnie i nie-offline.
 * W odróżnieniu od getEffectiveAvailableExchanges status 'degraded' NIE ukrywa giełdy —
 * ukrycie wycinało ją z FormData i publikacja po cichu szła na mniejszy zestaw.
 * Brak wpisu statusu / niezaładowany status = renderowana (dzisiejszy fallback).
 */
export const isExchangeRenderable = (
  profile: PortalProfile | null | undefined,
  exchange: unknown,
  sourceStatuses: MarketSourceStatuses,
  options: { statusLoaded?: boolean } = {}
): boolean => {
  const key = normalizeExchangeKey(exchange)
  if (!key || !isExchangeAllowed(profile, key)) {
    return false
  }
  if (!isStatusLoaded(options.statusLoaded)) {
    return true
  }
  const status = getMarketSourceRuntimeStatus(sourceStatuses, EXCHANGE_TO_MARKET_SOURCE[key])
  return status !== 'offline'
}

export const getRenderableExchanges = (
  profile: PortalProfile | null | undefined,
  sourceStatuses: MarketSourceStatuses,
  options: { statusLoaded?: boolean } = {}
): ExchangeKey[] =>
  SPOT_EXCHANGES.filter((exchange) => isExchangeRenderable(profile, exchange, sourceStatuses, options))

/** Giełdy renderowane, ale z problemem integracji (status 'degraded') — dostają badge ostrzeżenia. */
export const getDegradedExchanges = (
  profile: PortalProfile | null | undefined,
  sourceStatuses: MarketSourceStatuses,
  options: { statusLoaded?: boolean } = {}
): ExchangeKey[] => {
  if (!isStatusLoaded(options.statusLoaded)) {
    return []
  }
  return SPOT_EXCHANGES.filter((exchange) => {
    if (!isExchangeRenderable(profile, exchange, sourceStatuses, options)) {
      return false
    }
    return getMarketSourceRuntimeStatus(sourceStatuses, EXCHANGE_TO_MARKET_SOURCE[exchange]) === 'degraded'
  })
}

export const filterRenderableExchanges = (
  profile: PortalProfile | null | undefined,
  sourceStatuses: MarketSourceStatuses,
  exchanges: unknown,
  options: { statusLoaded?: boolean } = {}
): ExchangeKey[] => {
  const values = Array.isArray(exchanges) ? exchanges : exchanges != null ? [exchanges] : []
  const normalized: ExchangeKey[] = []
  for (const exchange of values) {
    const key = normalizeExchangeKey(exchange)
    if (!key || !isExchangeRenderable(profile, key, sourceStatuses, options) || normalized.includes(key)) {
      continue
    }
    normalized.push(key)
  }
  return normalized
}

export const getEffectiveAvailableExchanges = (
  profile: PortalProfile | null | undefined,
  sourceStatuses: MarketSourceStatuses,
  options: { statusLoaded?: boolean } = {}
): ExchangeKey[] =>
  SPOT_EXCHANGES.filter((exchange) =>
    isExchangeEffectivelyAvailable(profile, exchange, sourceStatuses, options)
  )

export const filterEffectiveAvailableExchanges = (
  profile: PortalProfile | null | undefined,
  sourceStatuses: MarketSourceStatuses,
  exchanges: unknown,
  options: { statusLoaded?: boolean } = {}
): ExchangeKey[] => {
  const values = Array.isArray(exchanges) ? exchanges : exchanges != null ? [exchanges] : []
  const normalized: ExchangeKey[] = []

  for (const exchange of values) {
    const key = normalizeExchangeKey(exchange)
    if (
      !key ||
      !isExchangeEffectivelyAvailable(profile, key, sourceStatuses, options) ||
      normalized.includes(key)
    ) {
      continue
    }
    normalized.push(key)
  }

  return normalized
}

export const isMarketSourceVisible = (
  profile: PortalProfile | null | undefined,
  source: string
): boolean => {
  if (source === 'SHIPPERS') {
    return true
  }
  const exchange = MARKET_SOURCE_TO_EXCHANGE[source]
  return exchange ? isExchangeAllowed(profile, exchange) : true
}

export const getVisibleMarketSources = (
  profile: PortalProfile | null | undefined,
  sources: readonly string[]
): string[] => sources.filter((source) => isMarketSourceVisible(profile, source))

export const isMarketSourceEffectivelyAvailable = (
  profile: PortalProfile | null | undefined,
  source: string,
  sourceStatuses: MarketSourceStatuses,
  options: { statusLoaded?: boolean } = {}
): boolean => {
  const normalizedSource = normalizeMarketSource(source)
  if (!normalizedSource) {
    return false
  }
  if (!isStatusLoaded(options.statusLoaded)) {
    return isMarketSourceVisible(profile, normalizedSource)
  }
  if (normalizedSource === 'SHIPPERS') {
    return isMarketSourceOnline(sourceStatuses, normalizedSource)
  }
  const exchange = MARKET_SOURCE_TO_EXCHANGE[normalizedSource]
  return exchange
    ? isExchangeEffectivelyAvailable(profile, exchange, sourceStatuses, options)
    : isMarketSourceOnline(sourceStatuses, normalizedSource)
}

export const getEffectiveAvailableMarketSources = (
  profile: PortalProfile | null | undefined,
  sources: readonly string[],
  sourceStatuses: MarketSourceStatuses,
  options: { statusLoaded?: boolean } = {}
): string[] =>
  sources.filter((source) =>
    isMarketSourceEffectivelyAvailable(profile, source, sourceStatuses, options)
  )

export const sanitizeMarketSourceFilters = (
  profile: PortalProfile | null | undefined,
  sources: Record<string, boolean> | null | undefined
): Record<string, boolean> => {
  const current = sources && typeof sources === 'object' ? sources : {}
  const next = { ...current }
  for (const [source, exchange] of Object.entries(MARKET_SOURCE_TO_EXCHANGE)) {
    if (!isExchangeAllowed(profile, exchange)) {
      next[source] = false
    }
  }
  return next
}
